import React, { useState } from 'react';
import { format, addDays, isSameDay, startOfWeek } from 'date-fns';
import { useOrg } from '../../hooks/useOrg';
import { api } from '../../lib/api';
import { X } from 'lucide-react';

const PRESET_COLORS = [
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#223368',
  '#EC4899', '#06B6D4', '#F97316', '#14B8A6', '#0DA7CA',
  '#DC2626', '#84CC16', '#A855F7', '#0EA5E9', '#6B7280',
];

export default function Sidebar({ selectedDate, onDateSelect, filters, onFilterChange, open, onClose, types = [], onTypesChanged, showPartnerAppts, onTogglePartnerAppts }) {
  const { currentOrg } = useOrg();
  const [showAddType, setShowAddType] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('#0DA7CA');
  const [saving, setSaving] = useState(false);

  const miniCalDays = Array.from({ length: 35 }, (_, i) =>
    addDays(startOfWeek(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1), { weekStartsOn: 1 }), i)
  );

  const handleAddType = async () => {
    if (!newLabel.trim() || !currentOrg) return;
    setSaving(true);
    try {
      const key = newLabel.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
      await api.createAppointmentType(currentOrg.id, {
        key,
        label: newLabel,
        color: newColor,
        sort_order: types.length,
      });
      setNewLabel('');
      setNewColor('#0DA7CA');
      setShowAddType(false);
      onTypesChanged?.();
    } catch (err) {
      console.error('Failed to add type:', err);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 bg-tertiary border border-strong rounded-lg text-primary-c text-[12px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA]';

  return (
    <div className={`${open ? '' : 'hidden lg:block'} lg:flex-shrink-0`}>
      {open && (
        <div className="fixed inset-0 bg-overlay z-30 lg:hidden" onClick={onClose} />
      )}

      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-[272px] bg-secondary border-r border-theme
        p-5 overflow-y-auto flex-shrink-0
        transform transition-transform duration-300 ease-in-out
        ${open ? '!block translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <button
          onClick={onClose}
          className="lg:hidden absolute top-4 right-4 w-8 h-8 rounded-lg bg-tertiary text-muted-c flex items-center justify-center hover:bg-hover-surface"
        >
          <X size={14} strokeWidth={2.2} />
        </button>

        {/* Mini Calendar */}
        <div className="mb-8">
          <h3 className="text-[10px] font-bold uppercase tracking-[1.2px] text-muted-c mb-3.5 pl-0.5">
            {format(selectedDate, 'MMMM yyyy')}
          </h3>
          <div className="grid grid-cols-7 gap-0.5 bg-tertiary p-2.5 rounded-[10px]">
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => (
              <div key={d} className="text-center text-[10px] font-semibold text-muted-c pb-1">{d}</div>
            ))}
            {miniCalDays.map((day, i) => {
              const isSelected = isSameDay(day, selectedDate);
              const isToday = isSameDay(day, new Date());
              const isOther = day.getMonth() !== selectedDate.getMonth();
              return (
                <button
                  key={i}
                  onClick={() => onDateSelect(day)}
                  className={`aspect-square flex items-center justify-center text-[12px] font-medium rounded-lg transition-all
                    ${isSelected ? 'bg-accent text-white font-semibold shadow-glow' : ''}
                    ${isToday && !isSelected ? 'bg-accent-glow text-accent-light font-bold' : ''}
                    ${isOther ? 'text-muted-c opacity-40' : ''}
                    ${!isSelected && !isToday && !isOther ? 'text-primary-c hover:bg-hover-surface' : ''}
                  `}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </div>

        {/* Appointment Type Filters */}
        <div>
          <h3 className="text-[10px] font-bold uppercase tracking-[1.2px] text-muted-c mb-3.5 pl-0.5">Appointment Types</h3>
          <div className="flex flex-col gap-0.5">
            {types.map(t => (
              <label
                key={t.key}
                className="flex items-center gap-2.5 text-[13px] text-secondary-c cursor-pointer py-[7px] px-2 rounded-[7px] hover:bg-hover-surface transition-colors"
              >
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={filters.includes(t.key)}
                  onChange={() => onFilterChange(t.key)}
                />
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t.color, boxShadow: `0 0 6px ${t.color}` }} />
                <span>{t.label}</span>
              </label>
            ))}
          </div>

          {/* Partner appointments toggle */}
          {onTogglePartnerAppts && (
            <label className="flex items-center gap-2.5 text-[13px] text-secondary-c cursor-pointer py-[7px] px-2 rounded-[7px] hover:bg-hover-surface transition-colors mt-2 border-t border-theme pt-3">
              <input
                type="checkbox"
                className="custom-checkbox"
                checked={showPartnerAppts}
                onChange={onTogglePartnerAppts}
              />
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#223368', boxShadow: '0 0 6px #223368' }} />
              <span>Partner Appointments</span>
            </label>
          )}

          {/* Add Type */}
          {showAddType ? (
            <div className="mt-3 p-3 bg-tertiary rounded-[10px] border border-theme animate-fade-in">
              <input
                type="text"
                className={inputClass}
                placeholder="Type name..."
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleAddType()}
              />
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {PRESET_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewColor(c)}
                    className={`w-5 h-5 rounded-md transition-all ${
                      newColor === c ? 'ring-2 ring-offset-1 ring-offset-[var(--bg-tertiary)] scale-110' : 'hover:scale-110'
                    }`}
                    style={{ background: c, ringColor: c }}
                  />
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleAddType}
                  disabled={saving || !newLabel.trim()}
                  className="flex-1 py-1.5 rounded-lg text-[11px] font-semibold text-white gradient-accent shadow-glow transition-all disabled:opacity-50"
                >
                  {saving ? 'Adding...' : 'Add'}
                </button>
                <button
                  onClick={() => { setShowAddType(false); setNewLabel(''); }}
                  className="flex-1 py-1.5 rounded-lg text-[11px] font-semibold text-muted-c bg-elevated border border-theme hover:bg-hover-surface transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddType(true)}
              className="mt-3 flex items-center gap-2 w-full px-2 py-2 rounded-[7px] text-[12px] font-medium text-muted-c hover:text-accent-light hover:bg-hover-surface transition-all"
            >
              <span className="text-[15px] leading-none">+</span>
              Add Appointment Type
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}
